// Trust org metrics — NEW console page (18H §4.2). Reads trust_org_metrics
// (D-33 daily rollup ingest) joined to the owning license/organization.
// Read-only: this is a telemetry mirror, not an editable record.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { RefreshCw, ShieldAlert, Eye, Users2, Globe2 } from 'lucide-react';
import { formatRelativeTime } from '@/lib/utils';
import { personaBase } from '@/lib/persona';
import { useLocation } from 'react-router';
import { Kpi } from '@/components/shared/Kpi';
import { StateFrame, classifyApiError, type LoadState } from '@/components/shared/StateFrame';
import { TableRowSkeleton } from '@/components/shared/SkeletonLoader';
import { Pill } from '@/components/shared/badges';
import { getOrgMetrics, getSaasDetails, orgNameFor, type TrustOrgMetricsRow } from '@/lib/api/trust';

const btnSec: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px',
  borderRadius: 8, background: 'var(--surface)', color: 'var(--text-primary)',
  border: '1px solid var(--border)', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
};

const th: React.CSSProperties = { padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '0.05em' };
const td: React.CSSProperties = { padding: '10px 12px', fontSize: 12.5 };

function YesNo({ v }: { v: boolean }) {
  return <span style={{ color: v ? '#16A34A' : 'var(--text-muted)', fontWeight: v ? 600 : 400 }}>{v ? 'Yes' : 'No'}</span>;
}

export function TrustOrgMetricsPage() {
  const location = useLocation();
  const base = personaBase(location.pathname);
  const [rows, setRows] = useState<TrustOrgMetricsRow[]>([]);
  const [tierByLicense, setTierByLicense] = useState<Record<string, string>>({});
  const [state, setState] = useState<LoadState>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [tierFilter, setTierFilter] = useState<string>('');
  const [staleOnly, setStaleOnly] = useState(false);

  const load = useCallback(async () => {
    setState('loading');
    setErrorMessage(null);
    try {
      const data = await getOrgMetrics();
      setRows(data || []);
      // Secondary fetch (tier, for the filter) — a failure here degrades the
      // filter to "unavailable" rather than nuking the metrics table (partial).
      try {
        const details = await getSaasDetails();
        const map: Record<string, string> = {};
        for (const d of details) map[d.license_id] = d.tier;
        setTierByLicense(map);
      } catch {
        setTierByLicense({});
      }
      setState('ready');
    } catch (err) {
      setRows([]);
      setState(classifyApiError(err));
      setErrorMessage(err instanceof Error ? err.message : 'Failed to load');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Telemetry-health signal (18E §4.1 Dashboard delta reused here at the org
  // grain): a row whose most recent ingest is >48h old means the daily
  // /telemetry/trust push has gone quiet for that org.
  const rollupSilent = (r: TrustOrgMetricsRow) => Date.now() - new Date(r.ingested_at).getTime() > 48 * 3600 * 1000;

  const filtered = useMemo(() => rows.filter((r) => {
    if (staleOnly && r.metrics.answers_stale <= 0) return false;
    if (tierFilter && tierByLicense[r.license_id] !== tierFilter) return false;
    return true;
  }), [rows, staleOnly, tierFilter, tierByLicense]);

  const kpis = useMemo(() => {
    const published = rows.filter((r) => r.metrics.published).length;
    const singleAdmin = rows.filter((r) => r.metrics.single_admin).length;
    const totalRing1 = rows.reduce((s, r) => s + (r.metrics.ring1_views || 0), 0);
    const openDisputes = rows.reduce((s, r) => s + (r.metrics.open_disputes || 0), 0);
    return { total: rows.length, published, singleAdmin, totalRing1, openDisputes };
  }, [rows]);

  const skeleton = (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 22 }}>
        {[0, 1, 2, 3].map((i) => <div key={i} style={{ height: 92, borderRadius: 12, background: 'var(--bg-muted)' }} />)}
      </div>
      <div className="card" style={{ padding: 0 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody>
            {[0, 1, 2, 3, 4].map((i) => <TableRowSkeleton key={i} cols={[120, 60, 60, 60, 70, 70, 60, 60, 90, 60, 70, 70]} />)}
          </tbody>
        </table>
      </div>
    </>
  );

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Trust org metrics</h1>
          <p className="page-subtitle">Latest daily rollup per hosted trust org (D-33) — aggregates only, never content or viewer identities.</p>
        </div>
        <button onClick={load} style={btnSec}><RefreshCw size={13} /> Refresh</button>
      </div>

      <div style={{ display: 'flex', gap: 16, marginBottom: 18, fontSize: 12.5 }}>
        <Link to={`${base}/trust/cost-guardrails`} style={{ color: 'var(--accent)', textDecoration: 'none', fontWeight: 500 }}>Cost guardrails →</Link>
        <Link to={`${base}/trust/domains-disputes`} style={{ color: 'var(--accent)', textDecoration: 'none', fontWeight: 500 }}>Domains &amp; disputes →</Link>
      </div>

      <StateFrame
        state={state}
        skeleton={skeleton}
        onRetry={load}
        errorMessage={errorMessage}
        empty={rows.length === 0}
        emptyIcon="bar-chart-3"
        emptyTitle="No trust org metrics yet"
        emptyBody="No daily rollup has landed via POST /telemetry/trust. This fills in once a hosted trust org has been provisioned and the platform's daily rollup job has run at least once."
      >
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 22 }}>
          <Kpi label="Trust orgs" value={kpis.total} icon="building-2" />
          <Kpi label="Published" value={kpis.published} icon="check-circle" accent="#16A34A" hint={`${kpis.total - kpis.published} unpublished`} />
          <Kpi label="Single-admin risk" value={kpis.singleAdmin} icon="user" accent={kpis.singleAdmin > 0 ? '#D97706' : undefined} hint="at-risk orgs" />
          <Kpi label="Ring-1 views (latest)" value={kpis.totalRing1.toLocaleString()} icon="eye" />
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <select value={tierFilter} onChange={(e) => setTierFilter(e.target.value)} style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, background: 'var(--surface)' }}>
            <option value="">All tiers</option>
            <option value="free">Free</option>
            <option value="trust_base">Trust Base</option>
            <option value="trust_pro">Trust Pro</option>
            <option value="enterprise">Enterprise</option>
          </select>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-secondary)', cursor: 'pointer' }}>
            <input type="checkbox" checked={staleOnly} onChange={(e) => setStaleOnly(e.target.checked)} style={{ accentColor: 'var(--accent)' }} />
            Has stale answers
          </label>
          {kpis.openDisputes > 0 && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#DC2626', fontWeight: 600 }}>
              <ShieldAlert size={13} /> {kpis.openDisputes} open domain dispute{kpis.openDisputes !== 1 ? 's' : ''} across orgs
            </span>
          )}
        </div>

        <div className="card" style={{ padding: 0 }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-secondary)' }}>
                  <th style={th}>Org</th>
                  <th style={th}>Tier</th>
                  <th style={th}>Published</th>
                  <th style={th}>Discoverable</th>
                  <th style={th}><Eye size={11} style={{ verticalAlign: -1 }} /> Teaser / Ring-1</th>
                  <th style={th}><Users2 size={11} style={{ verticalAlign: -1 }} /> Unique viewers</th>
                  <th style={th}>Requests / Grants</th>
                  <th style={th}>Answers stale</th>
                  <th style={th}>Single admin</th>
                  <th style={th}><Globe2 size={11} style={{ verticalAlign: -1 }} /> Domains (verified / grace)</th>
                  <th style={th}>Open disputes</th>
                  <th style={th}>Acquisition</th>
                  <th style={th}>Last ingest</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const silent = rollupSilent(r);
                  return (
                    <tr key={r.id} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={td}>
                        {r.license?.id
                          ? <Link to={`${base}/licenses/${r.license.id}`} style={{ color: 'var(--accent)', textDecoration: 'none', fontWeight: 500 }}>{orgNameFor(r)}</Link>
                          : orgNameFor(r)}
                      </td>
                      <td style={{ ...td, textTransform: 'capitalize', color: 'var(--text-secondary)' }}>{(tierByLicense[r.license_id] || '—').replace('_', ' ')}</td>
                      <td style={td}><YesNo v={r.metrics.published} /></td>
                      <td style={td}><YesNo v={r.metrics.discoverable} /></td>
                      <td style={td}>{r.metrics.teaser_views.toLocaleString()} / {r.metrics.ring1_views.toLocaleString()}</td>
                      <td style={td}>{r.metrics.unique_viewers.toLocaleString()}</td>
                      <td style={td}>{r.metrics.requests_received.toLocaleString()} / {r.metrics.grants_issued.toLocaleString()}</td>
                      <td style={td}>
                        {r.metrics.answers_stale > 0 ? (
                          <span style={{ color: '#D97706', fontWeight: 600 }}>{r.metrics.answers_stale}/{r.metrics.answers_total}</span>
                        ) : (
                          <span style={{ color: 'var(--text-muted)' }}>0/{r.metrics.answers_total}</span>
                        )}
                      </td>
                      <td style={td}>
                        {r.metrics.single_admin ? <Pill bg="var(--ps-pill-amber-bg)" fg="var(--ps-pill-amber-tx)">at risk</Pill> : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                      </td>
                      <td style={td}>
                        {r.metrics.verified_domains}
                        {r.metrics.domains_in_grace > 0 && <span style={{ color: '#D97706', marginLeft: 4 }}>({r.metrics.domains_in_grace} grace)</span>}
                      </td>
                      <td style={td}>
                        {r.metrics.open_disputes > 0 ? <span style={{ color: '#DC2626', fontWeight: 600 }}>{r.metrics.open_disputes}</span> : <span style={{ color: 'var(--text-muted)' }}>0</span>}
                      </td>
                      <td style={{ ...td, textTransform: 'capitalize', color: 'var(--text-secondary)' }}>{r.metrics.acquisition_source || '—'}</td>
                      <td style={td}>
                        <span style={{ color: silent ? '#D97706' : 'var(--text-muted)', fontWeight: silent ? 600 : 400 }} title={silent ? 'Rollup silence — no ingest in >48h' : undefined}>
                          {formatRelativeTime(r.ingested_at)}{silent ? ' ⚠' : ''}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </StateFrame>
    </div>
  );
}

export default TrustOrgMetricsPage;
